import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider,
} from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import UIDialogTitle from '../../Dialog/Title/UIDialogTitle';

const MAX_VALUE = 99999999.99;

const AdministrativeAdjustmentModal = ({
  open,
  handleModal,
  item,
  type, // 'CAIXA' or 'ESTOQUE'
  onSubmit,
}) => {
  const [tab, setTab] = useState(0);

  const initialValues = {
    mode: 'MOVIMENTACAO',
    operation: 'ENTRADA',
    value: 0,
    amount: 0,
    newTotal: type === 'CAIXA' ? (item ? item.currentValue : 0) : (item ? item.amount : 0),
    justification: '',
    cost: item && item.cost ? item.cost : 0,
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const formattedCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  return (
    <Dialog open={open} onClose={() => handleModal()} fullWidth maxWidth="sm">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          const payload = {
            id: item.id,
            justification: values.justification,
          };

          if (tab === 0) { // MOVIMENTAÇÃO
            payload.operation = values.operation;
            if (type === 'CAIXA') {
              payload.value = Number(values.value);
            } else {
              payload.amount = Number(values.amount);
              if (values.operation === 'ENTRADA') {
                payload.cost = Number(values.cost);
              }
            }
          } else { // CORREÇÃO
            const current = type === 'CAIXA' ? Number(item.currentValue) : Number(item.amount);
            const target = Number(values.newTotal);
            const diff = target - current;

            if (diff === 0) return; // No change

            payload.operation = diff > 0 ? 'ENTRADA' : 'SAIDA';
            if (type === 'CAIXA') {
              payload.value = Math.abs(diff);
            } else {
              payload.amount = Math.abs(diff);
              if (payload.operation === 'ENTRADA') {
                payload.cost = Number(values.cost);
              }
            }
          }
          onSubmit(payload);
        }}
      >
        {({ values, setFieldValue, submitForm }) => {
          const numValue = Number(values.value) || 0;
          const numAmount = Number(values.amount) || 0;
          const numNewTotal = Number(values.newTotal) || 0;
          const numCost = Number(values.cost) || 0;
          const currentTotal = type === 'CAIXA' ? Number(item ? item.currentValue : 0) : Number(item ? item.amount : 0);

          const isValueExceeded = numValue > MAX_VALUE;
          const isAmountExceeded = numAmount > MAX_VALUE;
          const isNewTotalExceeded = numNewTotal > MAX_VALUE || numNewTotal < 0;
          const isCostExceeded = numCost > MAX_VALUE || numCost < 0;

          const isSubmitDisabled =
            !values.justification ||
            isCostExceeded ||
            (tab === 0
              ? type === 'CAIXA'
                ? numValue <= 0 || isValueExceeded
                : numAmount <= 0 || isAmountExceeded
              : isNewTotalExceeded || currentTotal === numNewTotal);

          return (
            <Form>
              <UIDialogTitle id="form-dialog-title" onClose={() => handleModal()}>
                Ajuste Administrativo - {type === 'CAIXA' ? 'Caixa' : 'Estoque'}
              </UIDialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={4}>
                      <Typography variant="subtitle1" weight="bold">
                        {item ? `${item.name} (ID: ${item.id})` : ''}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {type === 'CAIXA'
                          ? `Saldo Atual: ${formattedCurrency(item ? item.currentValue : 0)}`
                          : `Estoque Atual: ${item ? item.amount : 0} unidades`}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Tabs
                      value={tab}
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab label="Movimentação" />
                      <Tab label="Correção de Saldo" />
                    </Tabs>
                  </Grid>

                  {tab === 0 ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="operation"
                          label="Tipo de Ajuste"
                          select
                          fullWidth
                          as={TextField}
                        >
                          <MenuItem value="ENTRADA">Entrada (Acréscimo)</MenuItem>
                          <MenuItem value="SAIDA">Saída (Retirada)</MenuItem>
                        </Field>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {type === 'CAIXA' ? (
                          <CurrencyTextField
                            label="Valor do Ajuste"
                            variant="standard"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            fullWidth
                            maximumValue="99999999.99"
                            error={isValueExceeded}
                            helperText={isValueExceeded ? 'Valor excede o limite máximo (R$ 99.999.999,99)' : ''}
                            value={values.value}
                            onChange={(ev, value) => setFieldValue('value', value)}
                          />
                        ) : (
                          <TextField
                            name="amount"
                            label="Quantidade"
                            type="number"
                            fullWidth
                            inputProps={{ min: 0.01, max: MAX_VALUE, step: 'any' }}
                            error={isAmountExceeded}
                            helperText={isAmountExceeded ? 'Quantidade excede o limite máximo (99.999.999,99)' : ''}
                            value={values.amount}
                            onChange={(ev) => setFieldValue('amount', ev.target.value)}
                          />
                        )}
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      {type === 'CAIXA' ? (
                        <CurrencyTextField
                          label="Novo Saldo Total"
                          variant="standard"
                          decimalCharacter=","
                          digitGroupSeparator="."
                          currencySymbol="R$"
                          outputFormat="string"
                          fullWidth
                          autoFocus
                          maximumValue="99999999.99"
                          error={isNewTotalExceeded}
                          helperText={
                            isNewTotalExceeded
                              ? 'Novo saldo excede o limite máximo (R$ 99.999.999,99) ou é negativo'
                              : 'O sistema calculará automaticamente o ajuste necessário para atingir este valor.'
                          }
                          value={values.newTotal}
                          onChange={(ev, value) => setFieldValue('newTotal', value)}
                        />
                      ) : (
                        <TextField
                          name="newTotal"
                          label="Nova Quantidade Total"
                          type="number"
                          fullWidth
                          autoFocus
                          inputProps={{ min: 0, max: MAX_VALUE, step: 'any' }}
                          error={isNewTotalExceeded}
                          helperText={
                            isNewTotalExceeded
                              ? 'Nova quantidade excede o limite máximo (99.999.999,99) ou é negativa'
                              : 'O sistema calculará automaticamente o ajuste necessário para atingir este valor.'
                          }
                          value={values.newTotal}
                          onChange={(ev) => setFieldValue('newTotal', ev.target.value)}
                        />
                      )}
                    </Grid>
                  )}

                  {type === 'ESTOQUE' && (tab === 0 ? values.operation === 'ENTRADA' : (Number(values.newTotal) > Number(item ? item.amount : 0))) && (
                    <Grid item xs={12}>
                      <CurrencyTextField
                        label="Custo Unitário (Opcional - usa o atual se zerado)"
                        variant="standard"
                        decimalCharacter=","
                        digitGroupSeparator="."
                        currencySymbol="R$"
                        outputFormat="string"
                        fullWidth
                        maximumValue="99999999.99"
                        error={isCostExceeded}
                        helperText={isCostExceeded ? 'Custo unitário excede o limite máximo (R$ 99.999.999,99)' : ''}
                        value={values.cost}
                        onChange={(ev, value) => setFieldValue('cost', value)}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Field
                      name="justification"
                      label="Justificativa (Obrigatório)"
                      placeholder="Ex: Quebra de produto, Sobra de caixa, etc."
                      fullWidth
                      multiline
                      rows={3}
                      as={TextField}
                      required
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleModal()} color="primary">
                  Cancelar
                </Button>
                <Button
                  onClick={submitForm}
                  color="primary"
                  variant="contained"
                  disabled={isSubmitDisabled}
                >
                  Confirmar Ajuste
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AdministrativeAdjustmentModal;
